import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useSubscriptionStore } from '@/stores/subscribe.js'
import { useClientStore } from '@shared/stores/client.js'
import { storeToRefs } from 'pinia'
import { formatPrice as formatPriceUtil } from '@/utils/stringParsing.js'
import useScreenSize from '@shared/composables/screenSize.js'

export function usePlanSelection() {
  const router = useRouter()
  const subscriptionStore = useSubscriptionStore()
  const clientStore = useClientStore()
  const { isTabletOrSmaller, screenWidth } = useScreenSize()

  const { membershipPlans, selectedPlanId } = storeToRefs(subscriptionStore)
  const { membershipDetails, plans } = storeToRefs(clientStore)

  const selectedHeroPlan = ref('')
  const selectedPaymentOption = ref(null)

  const forceTabletStyles = computed(() => {
    return screenWidth.value < 1100
  })

  const shouldUseTabletLayout = computed(() => {
    return isTabletOrSmaller.value || forceTabletStyles.value
  })

  const availableMonthlyPlans = computed(() => 
    membershipPlans.value?.filter(plan => 
      !plan.prepayment && 
      plan.basePrice > 0
    ) || []
  )

  const selectedPlan = computed(() => {
    return membershipPlans.value?.find(p => p.id === selectedPlanId.value)
  })

  const currentPlan = computed(() => {
    return plans.value?.currentBasePlan
  })

  const eligibleForChange = computed(() => 
    membershipDetails.value && membershipDetails.value.planChangeEligible
  )

  const ineligibilityReasonText = computed(() => {
    const reason = membershipDetails.value ? membershipDetails.value.planChangeIneligibleReason : null
    if (reason === 'OOC') {
      return `You can't change your membership because you have too many outstanding items.
      <br>Once we receive these items at our warehouse, you can switch to one of the follow plans.`
    }
    if (reason === 'PACKING_CASE') {
      return `You can't change your membership because we're packing your latest case. <br>Once your
      items have shipped, you can switch to one of the following plans.`
    }
    if (reason === 'RECENT_CHANGE') {
      return 'You recently changed your membership plan. <br>Please wait a bit longer before making another change. If you need assistance, please contact our support team.'
    }
    if (reason === 'NO_SUBSCRIPTION') {
      return 'You don\'t have an active subscription. <br>Please contact our support team if you need help reactivating your membership.'
    }
    if (reason === 'TRIALING') {
      return 'You can\'t change your membership while you\'re on a promotional trial period. <br>Once your trial ends, you\'ll be able to change your plan.'
    }
    if (reason === 'NEW_SUBSCRIPTION') {
      return 'You can\'t change your membership because your subscription is too new.'
    }
    if (reason) {
      return reason
    }
    return ''
  })

  const paymentOptionNames = (interval) => {
    switch (interval) {
      case 1:
        return 'Monthly'
      case 3:
        return 'Quarterly'
      case 6:
        return 'Semi-Annual'
      case 12:
        return 'Annual'
      default:
        return interval
    }
  }

  const chargeIntervalCopy = (plan) => {
    switch (plan.chargeInterval) {
      case 1:
        return 'every month'
      case 2:
      case 3:
      case 6:
      case 12:
        return `every ${plan.chargeInterval} months`
      default:
        return ''
    }
  }

  function paymentOptionsByNumItems(numItems = 4) {
    const sortOrder = [1, 12, 6, 3]
    return (membershipPlans.value || [])
      .filter(p => p.nItems === numItems)
      .slice()
      .sort((a, b) => {
        const aVal = Number(a.chargeInterval)
        const bVal = Number(b.chargeInterval)
        const aIndex = sortOrder.indexOf(aVal)
        const bIndex = sortOrder.indexOf(bVal)
        
        return aIndex - bIndex
      })
  }

  const visiblePaymentOptions = computed(() => {
    return paymentOptionsByNumItems(selectedPlan.value?.nItems) || []
  })

  const startingAtPrices = computed(() => {
    const result = {}
    const uniqueGroups = [...new Set(membershipPlans.value?.map(p => p.nItems))]

    uniqueGroups.forEach(numItems => {
      const plansInGroup = paymentOptionsByNumItems(numItems)
      if (!plansInGroup.length) return

      const monthlyPrices = plansInGroup.map(plan => plan.prepaymentEffectiveMonthlyPrice)
      const lowestPrice = Math.min(...monthlyPrices)
    
      result[numItems] = `$${Math.floor(lowestPrice / 100)}`
    })

    return result
  })



  function getPlanOrder(planId) {
    const plan = availableMonthlyPlans.value.find(plan => plan.id === planId)
    if (!plan) return 0
    
    switch (plan.nItems) {
      case 4:
        return 1
      case 7:
        return 2
      case 6: // unlimited
        return 3
      default:
        return 0
    }
  }

  const calloutPosition = computed(() =>
    selectedHeroPlan.value
      ? getPlanOrder(selectedHeroPlan.value) === 1
        ? 'start'
        : getPlanOrder(selectedHeroPlan.value) === 2
          ? 'center'
          : getPlanOrder(selectedHeroPlan.value) === 3
            ? 'end'
            : 'center'
      : 'center'
  )

  async function onPlanSelected(planId) {
    selectedHeroPlan.value = planId
    if (router.currentRoute.value.query.plan !== planId) {
      await router.replace({
        query: { ...router.currentRoute.value.query, plan: planId },
        state: { noScroll: true }
      })
    }
  }

  async function onPaymentOptionSelected(paymentOptionId) {
    selectedPaymentOption.value = paymentOptionId
    if (router.currentRoute.value.query.plan !== paymentOptionId) {
      await router.replace({
        query: { ...router.currentRoute.value.query, plan: paymentOptionId },
        state: { noScroll: true }
      })
    }
    await subscriptionStore.selectMembershipPlan({ planId: paymentOptionId, trackEvent: true })
  }

  const formatPrice = (price, showCents = true) => {
    return formatPriceUtil(price, !showCents, false)
  }

  const formatPlanPrice = (plan, showCents = true) => {
    return formatPrice(plan.basePrice, showCents)
  }

  const isCurrentPlan = (plan, currentPlanId) => {
    return currentPlanId === plan.id
  }

  const generatePriceLine = (plan, isCurrent, startingAtPrice = null) => {
    const contextualPriceFormatted = formatPlanPrice(plan, true)
    const isPartOfSelectedPlanGroup = paymentOptionsByNumItems(plan.nItems).some(p => p.id === selectedPlanId.value)
    const selectedPlan = paymentOptionsByNumItems(plan.nItems).find(p => p.id === selectedPlanId.value)
    if (isCurrent && !isPartOfSelectedPlanGroup) {
      return `${contextualPriceFormatted}/month.`
    } else if (isPartOfSelectedPlanGroup) {
      return `${formatPrice(selectedPlan.prepaymentEffectiveMonthlyPrice, true)}/month.`
    } else if (startingAtPrice) {
      return `Starting at ${startingAtPrice} a month.`
    }
    
    return `Starting at ${contextualPriceFormatted} a month.`
  }

  const getCustomDisplayName = (plan) => {
    const planGroup = paymentOptionsByNumItems(plan.nItems)
    const selectedPlan = planGroup.find(p => p.id === selectedPlanId.value)
    const numItemsText = selectedPlan.nItems === 6 ? 'Unlimited' : `${selectedPlan.nItems}-Item`
    if (selectedPlan) {
      return `${numItemsText} ${paymentOptionNames(selectedPlan.chargeInterval)} Plan`
    }
    return null
  }

  const shouldShowCustomDisplayName = (plan) => {
    const planGroup = paymentOptionsByNumItems(plan.nItems)
    return planGroup.some(p => p.id === selectedPlanId.value)
  }

  return {
    selectedHeroPlan,
    selectedPaymentOption,
    membershipPlans,
    availableMonthlyPlans,
    selectedPlan,
    selectedPlanId,
    currentPlan,
    eligibleForChange,
    ineligibilityReasonText,
    visiblePaymentOptions,
    startingAtPrices,
    calloutPosition,
    paymentOptionNames,
    chargeIntervalCopy,
    paymentOptionsByNumItems,
    getPlanOrder,
    onPlanSelected,
    onPaymentOptionSelected,
    formatPrice,
    formatPlanPrice,
    isCurrentPlan,
    generatePriceLine,
    getCustomDisplayName,
    shouldShowCustomDisplayName,
    shouldUseTabletLayout,
    selectMembershipPlan: subscriptionStore.selectMembershipPlan
  }
}
